import React, { useState } from 'react';
import HeroSection from '../components/shared/HeroSection';
import SectionTitle from '../components/shared/SectionTitle';
import { Card, CardContent } from '../components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';
import {
  Dialog,
  DialogContent,
} from '../components/ui/dialog';
import { heroImages, brands } from '../data/mock';
import { Watch, Flashlight, Droplets, Compass, X, ChevronDown } from 'lucide-react';
import { useCarousel } from '../hooks/useCarousel';

// Fallback images
const fallbackImages = [
  '/assets/images/accessoires-1.jpg',
  '/assets/images/accessoires-2.jpg',
];

const AccessoiresPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [expandedBrand, setExpandedBrand] = useState(null);
  const { images: dbImages } = useCarousel('accessoires');
  
  // Use database images or fallback
  const accessoiresCarouselImages = dbImages.length > 0 ? dbImages : fallbackImages;

  const toggleBrand = (brandName) => {
    setExpandedBrand(expandedBrand === brandName ? null : brandName);
  };
  
  const categories = [
    {
      icon: Watch,
      title: 'Montres GPS',
      description: 'Suivez vos performances avec précision grâce aux dernières technologies de tracking.'
    },
    {
      icon: Flashlight,
      title: 'Éclairage',
      description: 'Lampes frontales et accessoires de visibilité pour courir en toute sécurité.'
    },
    {
      icon: Droplets,
      title: 'Hydratation',
      description: 'Sacs, gourdes et ceintures pour rester hydraté pendant vos sorties.'
    },
    {
      icon: Compass,
      title: 'Autres accessoires',
      description: 'Chaussettes, casquettes, gants et tout le nécessaire pour vos sorties.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Lightbox Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-transparent border-none">
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-2 right-2 z-10 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Accessoires running"
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>

      <HeroSection
        image={heroImages.accessoires}
        title="Les Accessoires"
        subtitle="Tout l'équipement pour améliorer vos performances et votre confort"
        height="h-[50vh]"
      />

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-gray-600 leading-relaxed space-y-4 text-justify">
            <p>
              Qu'ils soient techniques, pratiques ou conçus pour booster vos performances, les accessoires adaptés deviennent vite de véritables partenaires de vos sorties running.
            </p>
            <p>
              Liberté et simplicité sont nos maîtres mots. Que vous soyez coureur amateur ou compétiteur, optimisez vos sensations et décuplez votre plaisir en complétant votre panoplie de runner.
            </p>
            <p>
              Passez en magasin et explorez notre sélection d'accessoires conçus pour le running, le trail, la marche nordique, le triathlon et le swimrun !
            </p>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle
            title="Nos catégories"
            subtitle="Trouvez l'accessoire qu'il vous faut"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <Card key={category.title} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-7 h-7 text-emerald-700" />
                    </div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-2">{category.title}</h3>
                    <p className="text-gray-600 text-sm">{category.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Carousel Section */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {accessoiresCarouselImages.map((image, index) => (
                <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <div 
                    className="overflow-hidden rounded-xl shadow-lg cursor-pointer"
                    onClick={() => setSelectedImage(image)}
                  >
                    <img
                      src={image}
                      alt={`Accessoires running ${index + 1}`}
                      className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="-left-4 lg:-left-12" />
            <CarouselNext className="-right-4 lg:-right-12" />
          </Carousel>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle
            title="Nos Marques"
            subtitle="Les marques références en accessoires de running"
          />
          <div className="mt-10">
            <img
              src="/assets/images/accessoires-marques.jpg"
              alt="Nos marques accessoires"
              className="w-full max-w-4xl mx-auto rounded-xl shadow-lg"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default AccessoiresPage;
